import type { ReactNode } from "react";
import { Help } from "./Help";

export interface FieldOptions {
  wide?: boolean;
  /** Marks the label with a red asterisk. */
  required?: boolean;
  /** Red frame: a required field left empty after the user started filling the form. */
  invalid?: boolean;
  /** Help text id (help.<id>). */
  help?: string;
}

export function Field({ label, children, wide, required, invalid, help }: { label: string; children: ReactNode } & FieldOptions) {
  const cls = ["field", wide && "wide", invalid && "invalid"].filter(Boolean).join(" ");
  return (
    <label className={cls}>
      <span className="field-label">
        {label}
        {required && (
          <span className="req" aria-hidden>
            {" "}*
          </span>
        )}
        {help && <Help id={help} />}
      </span>
      {children}
    </label>
  );
}

/** Text input bound to a string or null value. */
export function TextInput({
  label,
  value,
  onChange,
  type = "text",
  autoFocus,
  onBlur,
  ...options
}: {
  label: string;
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  type?: string;
  autoFocus?: boolean;
  /** Called when the field is left (e.g. to check a value only then, not while typing). */
  onBlur?: () => void;
} & FieldOptions) {
  return (
    <Field label={label} {...options}>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        aria-required={options.required || undefined}
        aria-invalid={options.invalid || undefined}
        autoFocus={autoFocus}
        onBlur={onBlur}
      />
    </Field>
  );
}
